import React, { ReactNode, useState } from 'react';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { SxProps } from '@mui/material';
import { CopyToClipboardIcon } from '../../Icons';
import { COLORS } from '../../theme';
import Button from '../Button';

export interface CopyToClipboardButtonProps {
  textToCopy: string;
  onClick?: () => void;
  customSX?: SxProps;
  text?: string;
  // Popover message after copying. The lib does not translate it: the consumer passes the translated text.
  copiedLabel?: ReactNode;
}

const POPOVER_DURATION = 1500;

const CopyToClipboardButton: React.FC<CopyToClipboardButtonProps> = ({
  textToCopy,
  onClick = () => {},
  customSX,
  text = '',
  copiedLabel = 'Copied',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyClick = () => {
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), POPOVER_DURATION);
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    handleCopyClick();
    onClick && onClick();
  };

  const id = copied ? 'copied-popover' : undefined;

  return (
    <>
      {text ? (
        <Button
          startIcon={<CopyToClipboardIcon />}
          isFullHeight={false}
          type="outline"
          onClick={handleButtonClick}
          color="tertiary"
        >
          {text}
        </Button>
      ) : (
        <IconButton onClick={handleButtonClick} sx={{ color: COLORS.gray[1000], ...customSX }}>
          <CopyToClipboardIcon />
        </IconButton>
      )}
      <Popover
        id={id}
        open={copied}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Typography sx={{ p: 1, background: COLORS.gray[1300], color: COLORS.gray[50] }}>
          {copiedLabel}
        </Typography>
      </Popover>
    </>
  );
};

export default CopyToClipboardButton;
